import React from 'react';
import { useQuery } from 'react-query';
import { reportService } from '../../services/reportService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  UsersIcon,
  CreditCardIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const Dashboard = () => {
  const { data, isLoading, error } = useQuery(
    'dashboard',
    () => reportService.getDashboardData(),
    { refetchInterval: 30000 } // Refresh every 30 seconds
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <h3 className="font-semibold">Error Loading Dashboard</h3>
        <p className="text-sm mt-1">Please check your connection and try again.</p>
      </div>
    );
  }

  const dashboardData = data?.data || {};
  const memberStats = dashboardData.member_statistics || {};
  const loanStats = dashboardData.loan_statistics || {};
  const todayCollection = dashboardData.today_collection || {};
  const dueLoans = dashboardData.due_loans || {};
  const collectionTrend = dashboardData.collection_trend || [];
  const loanPortfolio = dashboardData.loan_portfolio || [];
  const recentTransactions = dashboardData.recent_transactions || [];

  // Calculate growth percentages (mock for now, can be calculated from historical data)
  const memberGrowth = 12.5;
  const loanGrowth = 8.3;
  const collectionGrowth = 15.2;

  const stats = [
    {
      title: 'Total Members',
      value: memberStats.total_members || 0,
      subValue: `+${memberStats.today_joined || 0} today`,
      growth: memberGrowth,
      icon: UsersIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Active Loans',
      value: loanStats.active_loans || 0,
      subValue: `${loanStats.pending_loans || 0} pending approval`,
      growth: loanGrowth,
      icon: CreditCardIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: "Today's Collection",
      value: formatCurrency(todayCollection.total_collection || 0),
      subValue: `${todayCollection.collection_sheets || 0} collection sheets`,
      growth: collectionGrowth,
      icon: BanknotesIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'Due Loans (30+ days)',
      value: formatCurrency(dueLoans.due_amount || 0),
      subValue: `${dueLoans.due_loans_count || 0} members affected`,
      growth: -5.2,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600'
    }
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome back!</h1>
            <p className="text-blue-100">Here's what's happening with your samity today.</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-100">{formatDate(new Date())}</p>
          </div>
        </div>
      </div> */}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.bgColor} p-3 rounded-lg`}>
                  <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
                <div className={`flex items-center ${stat.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stat.growth >= 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                  )}
                  <span className="text-sm font-medium">{Math.abs(stat.growth)}%</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                {stat.subValue && (
                  <p className="text-xs text-gray-500 mt-2">{stat.subValue}</p>
                )}
              </div>
            </div>
            <div className={`h-1 ${stat.color}`}></div>
          </div>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl mb-1">💰</div>
          <div className="text-lg font-bold text-gray-900">{formatCurrency(loanStats.outstanding_amount || 0)}</div>
          <div className="text-xs text-gray-500">Outstanding Loans</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl mb-1">🏦</div>
          <div className="text-lg font-bold text-gray-900">{formatCurrency(memberStats.total_savings || 0)}</div>
          <div className="text-xs text-gray-500">Total Savings</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl mb-1">📊</div>
          <div className="text-lg font-bold text-gray-900">{memberStats.pending_approval || 0}</div>
          <div className="text-xs text-gray-500">Pending Approvals</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-2xl mb-1">✅</div>
          <div className="text-lg font-bold text-gray-900">{loanStats.total_loans || 0}</div>
          <div className="text-xs text-gray-500">Total Loans Processed</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Collection Trend Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Collection Trend</h3>
            <div className="flex items-center text-sm text-gray-500">
              <CalendarIcon className="h-4 w-4 mr-1" />
              Last 7 days
            </div>
          </div>
          {collectionTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={collectionTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" tickFormatter={(value) => `৳${value/1000}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#3b82f6"
                  fill="#93c5fd"
                  fillOpacity={0.3}
                  name="Collection"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              No collection data available
            </div>
          )}
        </div>

        {/* Loan Portfolio Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Loan Portfolio by Type</h3>
          {loanPortfolio.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={loanPortfolio}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {loanPortfolio.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              No loan data available
            </div>
          )}
        </div>
      </div>

      {/* Second Row of Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recovery Rate */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Loan Recovery Rate</h3>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-48 h-48">
                  <circle
                    className="text-gray-200"
                    strokeWidth="12"
                    stroke="currentColor"
                    fill="transparent"
                    r="88"
                    cx="96"
                    cy="96"
                  />
                  <circle
                    className="text-green-500"
                    strokeWidth="12"
                    strokeDasharray={2 * Math.PI * 88}
                    strokeDashoffset={2 * Math.PI * 88 * (1 - 0.75)}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="88"
                    cx="96"
                    cy="96"
                  />
                </svg>
                <span className="absolute text-3xl font-bold text-green-600">75%</span>
              </div>
              <p className="text-sm text-gray-500 mt-4">Overall recovery rate</p>
              <div className="mt-2 text-xs text-gray-400">Target: 95%</div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h3>
          {recentTransactions.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {recentTransactions.map((transaction, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'deposit' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {transaction.type === 'deposit' ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{transaction.member_name}</p>
                      <p className="text-xs text-gray-500">{formatDate(transaction.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${
                      transaction.type === 'deposit' ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {transaction.type === 'deposit' ? '+' : ''}{formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{transaction.type}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              No recent transactions
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 bg-blue-50 rounded-lg text-center hover:bg-blue-100 transition-colors">
            <UsersIcon className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Add Member</p>
          </button>
          <button className="p-4 bg-green-50 rounded-lg text-center hover:bg-green-100 transition-colors">
            <CreditCardIcon className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">New Loan</p>
          </button>
          <button className="p-4 bg-purple-50 rounded-lg text-center hover:bg-purple-100 transition-colors">
            <BanknotesIcon className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Daily Collection</p>
          </button>
          <button className="p-4 bg-orange-50 rounded-lg text-center hover:bg-orange-100 transition-colors">
            <CurrencyDollarIcon className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Open Savings</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;